import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { vehiclesAPI, servicesAPI, paymentsAPI, transfersAPI } from '../services/api';
import VehicleIllustration from '../components/VehicleIllustration';
import ServiceModal from '../components/ServiceModal';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';

// [NEW] Ícones de linha pro badge "Foto ✓" / "Vídeo ✓" do histórico de serviços — mesmo
// padrão de layout já usado nos chips de Tipo de Serviço (ServiceModal.jsx) e nos botões
// do topo desta página: viewBox 24x24, stroke=currentColor, strokeWidth 2, pontas/junções
// arredondadas. Substituem os emojis 📷/🎥/✓, que rendiam de formas bem diferentes (às
// vezes feias) dependendo do sistema operacional.
const MEDIA_BADGE_ICONS = {
  camera: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  video: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  check: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
};

function fmtDate(dateStr) {
  if (!dateStr) return { day: '--', month: '---' };
  // performed_at é uma data "de calendário" (o dia que o usuário escolheu, sem hora real —
  // o backend guarda/retorna como DATE, que o driver do Postgres serializa em UTC meia-noite,
  // ex: "2026-08-31T00:00:00.000Z"). Se a gente deixar o JS converter isso pro fuso local,
  // em qualquer fuso negativo (Brasília = UTC-3) o horário vira 21h do dia ANTERIOR e a UI
  // mostra "30 AGO" pra um serviço registrado com data 31/08. Por isso extraímos ano/mês/dia
  // direto da string, sem passar pelo parser de fuso do Date.
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return { day: '--', month: '---' };
  const [, year, month, day] = match;
  const monthName = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
    .toLocaleString('pt-BR', { month: 'short', timeZone: 'UTC' })
    .replace('.', '')
    .toUpperCase();
  return { day, month: monthName };
}

// Formata um valor em reais no padrão brasileiro: separador de milhar "." e sempre duas
// casas decimais de centavos, ex: 500001 → "500.001,00", 67588.5 → "67.588,50".
// Usar sempre isso (nunca Math.round nem toLocaleString cru) em qualquer lugar que mostrar
// `cost` — Math.round descarta os centavos, e toLocaleString('pt-BR') sozinho só mostra
// decimais quando o valor por acaso não é redondo, o que deixava a formatação inconsistente
// entre um serviço e outro.
function formatBRL(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VehicleDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const m = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [access, setAccess] = useState(null); // 'free' | 'paid' | 'view_only' | null (ainda carregando)
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteVehicle, setShowDeleteVehicle] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingVehicle, setDeletingVehicle] = useState(false);
  // [NEW] Repasse de veículo — ver services/api.js (transfersAPI) e backend/routes/transfers.js
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferLink, setTransferLink] = useState(null);
  const [generatingTransfer, setGeneratingTransfer] = useState(false);
  const [cancellingTransfer, setCancellingTransfer] = useState(false);
  // [NEW] Preview de foto/vídeo de comprovação do serviço — ver ícones no histórico abaixo.
  const [mediaPreview, setMediaPreview] = useState(null); // { type: 'photo'|'video', url } | null

  const loadData = () => {
    Promise.all([
      vehiclesAPI.get(id),
      // [FIX] Mesma checagem de acesso do GaragePage — sem isso, essa página (que é
      // justamente pra onde o Stripe redireciona quando o Checkout é cancelado) deixava
      // registrar serviço e editar o veículo mesmo sem a assinatura confirmada.
      paymentsAPI.vehicleAccess().then(r => r.access || []).catch(() => []),
    ])
      .then(([vehicleData, accessList]) => {
        setData(vehicleData);
        const entry = accessList.find(a => a.vehicle_id === id);
        setAccess(entry ? entry.access : 'free');
      })
      .catch(() => { toast.error('Veículo não encontrado'); navigate('/garagem'); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadData(); }, [id]); // eslint-disable-line

  // Feedback do retorno do Stripe (é pra cá que o Checkout manda de volta, com ou sem sucesso)
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment) return;
    if (payment === 'success') {
      toast.success('Assinatura confirmada! Veículo liberado. 🎉');
    } else if (payment === 'cancelled') {
      toast('Pagamento não concluído — o veículo continua com acesso restrito até você finalizar.', { icon: '⚠️' });
    }
    searchParams.delete('payment');
    setSearchParams(searchParams, { replace: true });
    loadData();
    // eslint-disable-next-line
  }, []);

  const pending = access === 'view_only';

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const { url } = await paymentsAPI.createCheckout(id);
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Não foi possível iniciar o pagamento. Tente novamente.');
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao iniciar a assinatura do veículo');
    } finally {
      setCheckingOut(false);
    }
  };

  // [NEW] Repasse de veículo — a NICO nunca media a venda, só gera um link/código pra
  // o dono atual mandar (WhatsApp etc.) pra quem vai receber o histórico.
  const handleGenerateTransfer = async () => {
    setGeneratingTransfer(true);
    try {
      const res = await transfersAPI.initiate(id);
      setTransferLink(res);
      setTransferModalOpen(true);
    } catch (err) {
      toast.error(err.message || 'Erro ao gerar link de repasse');
    } finally {
      setGeneratingTransfer(false);
    }
  };

  const handleCancelTransfer = async () => {
    setCancellingTransfer(true);
    try {
      await transfersAPI.cancelPending(id);
      toast.success('Link de repasse cancelado');
      setTransferModalOpen(false);
      setTransferLink(null);
    } catch (err) {
      toast.error(err.message || 'Erro ao cancelar repasse');
    } finally {
      setCancellingTransfer(false);
    }
  };

  const copyTransferLink = () => {
    if (!transferLink?.url) return;
    navigator.clipboard.writeText(transferLink.url)
      .then(() => toast.success('Link copiado!'))
      .catch(() => toast.error('Não foi possível copiar — copie manualmente'));
  };

  const handleEdit = (service) => {
    if (pending) return;
    setEditingService(service);
    setServiceModalOpen(true);
  };

  const handleNewService = () => {
    if (pending) return;
    setEditingService(null);
    setServiceModalOpen(true);
  };

  const handleCloseModal = () => {
    setServiceModalOpen(false);
    setEditingService(null);
  };

  const handleDeleteVehicle = async () => {
    setDeletingVehicle(true);
    try {
      await vehiclesAPI.delete(id);
      toast.success('Veículo removido da garagem');
      navigate('/garagem');
    } catch (err) {
      toast.error(err.message || 'Erro ao remover veículo');
    } finally {
      setDeletingVehicle(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Tem certeza que deseja remover este serviço?')) return;
    setDeletingId(serviceId);
    try {
      await servicesAPI.delete(serviceId);
      toast.success('Serviço removido');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao remover serviço');
    } finally {
      setDeletingId(null);
    }
  };

  // [FIX] Serviço de dono anterior (is_previous_owner, ver GET /vehicles/:id) nunca é
  // editável — nem pelo novo dono, nem pelo vendedor no modo somente-leitura.
  // [FIX GRAVE 30/ago/2026] `readOnly` (definido logo abaixo) precisa entrar aqui
  // também, em defesa de profundidade — o backend agora já filtra e nunca manda pro
  // vendedor um serviço pós-venda pra começo de conversa (ver GET /vehicles/:id), mas
  // essa função não deveria depender só disso pra nunca desenhar Editar/Excluir na
  // tela de alguém que está em modo somente-leitura.
  const canEdit = (s) => s.validation_status !== 'validated' && !pending && !readOnly && !s.is_previous_owner;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '12px' }}><div className="spinner" style={{ width: '32px', height: '32px' }} /></div>;
  if (!data) return null;

  const { vehicle, services = [], alerts = [], read_only: readOnly = false, sold_at: soldAt = null } = data;
  const activeAlerts = alerts.filter(a => a.is_active);
  // Soma em centavos (números inteiros) antes de voltar pra reais — soma direto em ponto
  // flutuante (0.1 + 0.2 !== 0.3 em JS) pode acumular um errinho de centavo com muitos
  // serviços somados. Arredondando pra centavo em cada item e somando como inteiro, o total
  // fecha exato.
  const totalCostCents = services.reduce((sum, s) => sum + Math.round((Number(s.cost) || 0) * 100), 0);
  const totalCost = totalCostCents / 100;
  const avgCost = services.length > 0 ? totalCostCents / services.length / 100 : 0;
  const costPerKm = vehicle.current_km > 0 ? totalCost / vehicle.current_km : 0;

  // [FIX 01/set/2026] Os 4 botões do hero (Registrar serviço / Gerar relatório / Repassar /
  // Excluir) foram entrando um de cada vez, cada um com seu próprio estilo inline — no mobile
  // isso quebrava em linhas desiguais (2 botões numa linha, depois 1 sozinho "sobrando" à
  // esquerda, depois outro sozinho), com espaço vazio feio à direita. Um estilo-base
  // compartilhado + grid de 2 colunas no mobile (cada botão ocupa 100% da própria célula)
  // resolve isso: sempre 2 por linha, do mesmo tamanho, sem sobra. No desktop continua em
  // linha única, do jeito que já estava (isso já ficava bem ali).
  const heroBtnBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    padding: m ? '12px 14px' : '12px 22px', borderRadius: '12px', fontWeight: 700,
    fontSize: '13px', fontFamily: "'DM Sans', sans-serif", border: 'none',
    width: m ? '100%' : 'auto', boxSizing: 'border-box', textDecoration: 'none',
  };

  return (
    <div>
      {/* Hero */}
      <div style={{ background: '#14562e', color: '#fff', borderRadius: m ? '16px' : '22px', padding: m ? '18px' : '32px', display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr auto', gap: '20px', alignItems: 'center', position: 'relative', overflow: 'hidden', marginBottom: pending ? '0' : '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(233,113,23,.20), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <button onClick={() => navigate('/garagem')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,.7)', fontSize: '13px', fontWeight: 600, marginBottom: '10px', cursor: 'pointer', background: 'none', border: 'none', fontFamily: "'DM Sans', sans-serif" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Voltar para garagem
          </button>
          <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: m ? '22px' : '44px', letterSpacing: '-0.02em', margin: '0 0 6px', lineHeight: 0.95, color: '#fff', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {vehicle.nickname || `${vehicle.brand} ${vehicle.model}`}
          </h1>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
            {vehicle.plate && <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, background: '#111', color: '#fff', padding: '4px 12px', borderRadius: '6px', fontSize: m ? '12px' : '14px', letterSpacing: '1.8px' }}>{vehicle.plate}</span>}
            <span style={{ background: 'rgba(255,255,255,.12)', padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600 }}>{vehicle.year}</span>
            {vehicle.color && <span style={{ background: 'rgba(255,255,255,.12)', padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600 }}>{vehicle.color}</span>}
            <span style={{ background: 'rgba(255,255,255,.12)', padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600 }}>{vehicle.current_km?.toLocaleString('pt-BR')} km</span>
            {pending && <span style={{ background: '#e97117', padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700 }}>🔒 Pagamento pendente</span>}
            {readOnly && <span style={{ background: 'rgba(255,255,255,.18)', padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700 }}>👁️ Somente leitura · veículo vendido</span>}
          </div>
          <div style={{ display: m ? 'grid' : 'flex', gridTemplateColumns: m ? '1fr 1fr' : undefined, gap: '8px', marginTop: '16px', flexWrap: m ? undefined : 'wrap' }}>
            {!pending && !readOnly && (
              <button onClick={handleNewService} style={{ ...heroBtnBase, background: '#fff', color: '#14562e', cursor: 'pointer' }}>
                + Registrar serviço
              </button>
            )}
            {!pending && !readOnly && (
              <button style={{ ...heroBtnBase, background: 'rgba(255,255,255,.14)', color: '#fff', border: '1px solid rgba(255,255,255,.2)', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                Gerar relatório
              </button>
            )}
            {!pending && !readOnly && (
              <button onClick={handleGenerateTransfer} disabled={generatingTransfer}
                style={{ ...heroBtnBase, background: 'rgba(255,255,255,.14)', color: '#fff', border: '1px solid rgba(255,255,255,.2)', cursor: generatingTransfer ? 'default' : 'pointer', opacity: generatingTransfer ? 0.7 : 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
                {generatingTransfer ? 'Gerando link…' : 'Repassar veículo'}
              </button>
            )}
            {!readOnly && (
              <button onClick={() => { setShowDeleteVehicle(true); setDeleteConfirmText(''); }}
                style={{ ...heroBtnBase, background: 'rgba(200,50,40,.2)', color: '#ffc9c4', border: '1px solid rgba(200,50,40,.3)', cursor: 'pointer' }}
                onMouseOver={e => { e.currentTarget.style.background = 'rgba(200,50,40,.35)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'rgba(200,50,40,.2)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                Excluir veículo
              </button>
            )}
          </div>
        </div>
        {!m && <div style={{ position: 'relative', zIndex: 1, opacity: 0.95 }}><VehicleIllustration type={vehicle.type} color="#fff" size={160} /></div>}
      </div>

      {/* Aviso de pagamento pendente */}
      {pending && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', background: '#fdecd8', border: '1px solid rgba(233,113,23,0.3)', borderRadius: m ? '0 0 16px 16px' : '0 0 22px 22px', padding: m ? '16px 18px' : '18px 32px', marginBottom: '24px' }}>
          <span style={{ fontSize: '13px', color: '#7a3d10', lineHeight: 1.5, flex: 1, minWidth: '240px' }}>
            Esse veículo passou do limite gratuito de 3 e ainda não tem a assinatura confirmada (R$ 9,90/mês). Enquanto isso, fica só pra visualização — registrar serviço e editar dados ficam bloqueados.
          </span>
          <button
            onClick={startCheckout}
            disabled={checkingOut}
            style={{ padding: '10px 18px', borderRadius: '10px', fontWeight: 700, fontSize: '13px', cursor: checkingOut ? 'default' : 'pointer', border: 'none', background: '#e97117', color: '#fff', fontFamily: "'DM Sans', sans-serif", opacity: checkingOut ? 0.7 : 1, whiteSpace: 'nowrap' }}
          >
            {checkingOut ? 'Abrindo pagamento…' : 'Resolver pagamento →'}
          </button>
        </div>
      )}

      {/* [NEW] Aviso de veículo já repassado — vendedor vendo o próprio histórico antigo */}
      {readOnly && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', background: '#f4ede0', border: '1px solid #e5ddc9', borderRadius: m ? '0 0 16px 16px' : '0 0 22px 22px', padding: m ? '16px 18px' : '18px 32px', marginBottom: '24px' }}>
          <span style={{ fontSize: '13px', color: '#4a5147', lineHeight: 1.5, flex: 1, minWidth: '240px' }}>
            👁️ Você repassou este veículo{soldAt ? ` em ${new Date(soldAt).toLocaleDateString('pt-BR')}` : ''}. Esse é o histórico de tudo que você mesmo registrou até essa data — o que acontece com o carro dali pra frente é com o novo dono.
          </span>
        </div>
      )}

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '2fr 1fr', gap: '20px' }}>
        {/* Timeline */}
        <div style={{ background: '#fff', border: '1px solid #e5ddc9', borderRadius: '16px', padding: m ? '18px' : '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '16px', color: '#14562e', margin: 0 }}>Histórico de serviços · {services.length}</h3>
          </div>
          {services.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '36px', opacity: 0.3, marginBottom: '12px' }}>🔧</div>
              <p style={{ color: '#6a7268', fontSize: '14px', margin: 0 }}>Nenhum serviço registrado</p>
            </div>
          ) : services.map((s, i) => {
            const dt = fmtDate(s.performed_at);
            const editable = canEdit(s);
            return (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: m ? 'auto 1fr' : 'auto 1fr auto', gap: m ? '10px' : '16px', padding: '14px 0', borderBottom: i < services.length - 1 ? '1px solid #efe8d6' : 'none', position: 'relative', background: s.is_previous_owner ? '#faf8f2' : 'transparent', borderRadius: s.is_previous_owner ? '10px' : 0 }}>
                <div style={{ textAlign: 'center', minWidth: '44px' }}>
                  <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '18px', color: '#14562e', display: 'block' }}>{dt.day}</span>
                  <small style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#6a7268', fontWeight: 600 }}>{dt.month}</small>
                </div>
                <div>
                  <h4 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '14px', margin: '0 0 3px', color: '#183d24' }}>
                    {s.service_type_name || s.custom_service_name || 'Serviço'}
                    {s.validation_status === 'validated' && <span style={{ background: '#2f8f52', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', marginLeft: '6px' }}>✓ Verificado</span>}
                    {s.validation_status === 'pending' && <span style={{ background: '#e97117', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', marginLeft: '6px' }}>Aguardando</span>}
                    {s.is_previous_owner && <span style={{ background: '#6a7268', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', marginLeft: '6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>🔒 Dono anterior</span>}
                    {/* [NEW] Sinalização de foto/vídeo de comprovação anexado ao serviço (ver
                        ServiceModal.jsx). Badge-pílula (fundo verde claro #e6f0e9, texto verde
                        escuro #14562e, check ✓) — esse é o PADRÃO de layout pra esse tipo de
                        elemento (indicador de mídia anexada); a mesma receita de estilo é
                        replicada no ícone da tela do relatório, ver R.mediaBadge em
                        ReportsPage.jsx. Só aparece quando existe mídia — a maioria dos serviços
                        não tem, e a lista continua limpa pra eles. Clicar abre o preview em
                        tamanho maior (ver modal no fim do arquivo). Texto fica no singular
                        ("Foto"/"Vídeo") porque hoje o NICO só aceita uma foto e um vídeo por
                        serviço — não é uma contagem. */}
                    {s.photo_url && (
                      <button
                        onClick={() => setMediaPreview({ type: 'photo', url: s.photo_url })}
                        title="Ver foto do reparo"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e6f0e9', color: '#14562e', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', marginLeft: '6px', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", verticalAlign: 'middle' }}
                      >
                        {MEDIA_BADGE_ICONS.camera} Foto {MEDIA_BADGE_ICONS.check}
                      </button>
                    )}
                    {s.video_url && (
                      <button
                        onClick={() => setMediaPreview({ type: 'video', url: s.video_url })}
                        title="Ver vídeo do reparo"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e6f0e9', color: '#14562e', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', marginLeft: '6px', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", verticalAlign: 'middle' }}
                      >
                        {MEDIA_BADGE_ICONS.video} Vídeo {MEDIA_BADGE_ICONS.check}
                      </button>
                    )}
                  </h4>
                  <div style={{ fontSize: '12px', color: '#6a7268' }}>
                    {s.professional_name && <span>📍 {s.professional_name}</span>}
                    {s.km_at_service > 0 && <span> · {Number(s.km_at_service).toLocaleString('pt-BR')} km</span>}
                    {/* [NEW] Data em que o registro foi de fato incluído no NICO (services.created_at),
                        discreta e separada da data do serviço em si (performed_at, digitada pelo usuário
                        e mostrada em destaque à esquerda). Ajuda a diferenciar, olhando o histórico, um
                        serviço lançado no mesmo dia em que aconteceu de um lançado bem depois (ex: usuário
                        cadastra hoje um serviço que diz ter feito há 3 anos) — sem acusar nada, só deixando
                        a informação visível pra quem for avaliar o histórico (comprador, mecânico, etc). */}
                    {s.created_at && <span style={{ color: '#a8a29a' }}> · cadastrado em {new Date(s.created_at).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  {/* Ações: Editar / Excluir (só serviços não validados, e só com pagamento em dia) */}
                  {editable && (
                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                      <button
                        onClick={() => handleEdit(s)}
                        style={{ background: 'none', border: 'none', color: '#14562e', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        onMouseOver={e => { e.currentTarget.style.color = '#e97117'; }}
                        onMouseOut={e => { e.currentTarget.style.color = '#14562e'; }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        disabled={deletingId === s.id}
                        style={{ background: 'none', border: 'none', color: '#999', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: deletingId === s.id ? 0.4 : 1 }}
                        onMouseOver={e => { e.currentTarget.style.color = '#c94a2f'; }}
                        onMouseOut={e => { e.currentTarget.style.color = '#999'; }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        {deletingId === s.id ? 'Removendo...' : 'Excluir'}
                      </button>
                    </div>
                  )}
                  {m && s.cost && <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '14px', color: '#14562e', marginTop: '4px' }}>R$ {formatBRL(s.cost)}</div>}
                </div>
                {!m && s.cost && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '16px', color: '#14562e' }}>R$ {formatBRL(s.cost)}</div>
                    <small style={{ fontSize: '11px', color: '#6a7268', fontWeight: 600, textTransform: 'uppercase' }}>gasto</small>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Side cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Próximos serviços */}
          <div style={{ background: '#fff', border: '1px solid #e5ddc9', borderRadius: '16px', padding: m ? '18px' : '24px' }}>
            <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '15px', color: '#14562e', margin: '0 0 14px' }}>Próximos serviços</h3>
            {activeAlerts.length === 0 ? <p style={{ color: '#6a7268', fontSize: '13px', margin: 0 }}>Tudo em dia! ✅</p> : activeAlerts.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f4ede0', borderRadius: '12px', marginBottom: '8px', borderLeft: '3px solid #e97117' }}>
                <div style={{ flex: 1 }}>
                  <b style={{ fontSize: '13px', color: '#14562e', display: 'block' }}>{a.title || a.service_name || 'Manutenção'}</b>
                  <small style={{ fontSize: '12px', color: '#6a7268' }}>{a.message || 'Em breve'}</small>
                </div>
                <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '12px', color: '#e97117' }}>Em breve</span>
              </div>
            ))}
          </div>

          {/* Resumo financeiro */}
          <div style={{ background: '#fff', border: '1px solid #e5ddc9', borderRadius: '16px', padding: m ? '18px' : '24px' }}>
            <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '15px', color: '#14562e', margin: '0 0 14px' }}>Resumo financeiro</h3>
            {/* [FIX 31/ago/2026] As três linhas usavam formatações diferentes e inconsistentes
                (Math.round descartava os centavos da média; as outras duas só mostravam decimais
                quando o valor não era redondo por acaso). Agora as três usam formatBRL, sempre
                com separador de milhar e duas casas de centavo. */}
            {[['Este ano', totalCost], ['Média por serviço', avgCost], ['Custo por km', costPerKm]].map(([label, val], i) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 2 ? '1px solid #efe8d6' : 'none' }}>
                <span style={{ color: '#6a7268', fontSize: '13px' }}>{label}</span>
                <b style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, color: '#14562e' }}>
                  R$ {formatBRL(val)}
                </b>
              </div>
            ))}
          </div>

          {/* Dica do Nico */}
          <div style={{ background: '#14562e', color: '#fff', borderRadius: '16px', padding: m ? '18px' : '24px' }}>
            <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '15px', margin: '0 0 8px' }}>🐕 Fala aí, Nico!</h3>
            <p style={{ fontSize: '13px', lineHeight: 1.5, color: 'rgba(255,255,255,.85)', margin: 0 }}>
              {vehicle.current_km > 60000 ? `Próximo dos ${Math.ceil(vehicle.current_km / 10000) * 10000} km. Hora de checar velas, filtro e correia.` : `Mantenha o histórico atualizado para valorizar seu ${vehicle.brand} ${vehicle.model}!`}
            </p>
            <button onClick={() => navigate('/profissionais')} style={{ background: '#e97117', color: '#fff', width: '100%', padding: '12px', borderRadius: '12px', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginTop: '14px' }}>Ver checklist completo</button>
          </div>
        </div>
      </div>

      {/* Modal de confirmação para excluir veículo */}
      {showDeleteVehicle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowDeleteVehicle(false); }}>
          <div style={{ background: '#fff', borderRadius: '22px', padding: m ? '24px' : '32px', maxWidth: '460px', width: '100%', boxShadow: '0 20px 48px rgba(15,64,35,.14)', boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '28px' }}>🗑️</div>
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '20px', color: '#c94a2f', margin: '0 0 6px' }}>Excluir veículo</h3>
              <p style={{ fontSize: '14px', color: '#6a7268', margin: 0, lineHeight: 1.5 }}>
                Isso vai remover o <b style={{ color: '#183d24' }}>{vehicle.brand} {vehicle.model}</b> e todo o histórico de serviços. Essa ação não pode ser desfeita.
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#6a7268', display: 'block', marginBottom: '6px' }}>
                Digite <b style={{ color: '#c94a2f' }}>EXCLUIR</b> para confirmar
              </label>
              <input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="EXCLUIR"
                autoFocus
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e5ddc9', background: '#fafaf8', fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 700, letterSpacing: '2px', textAlign: 'center', outline: 'none', boxSizing: 'border-box', color: deleteConfirmText === 'EXCLUIR' ? '#c94a2f' : '#183d24' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowDeleteVehicle(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1.5px solid #e5ddc9', background: '#fff', color: '#183d24', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                Cancelar
              </button>
              <button
                onClick={handleDeleteVehicle}
                disabled={deleteConfirmText !== 'EXCLUIR' || deletingVehicle}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: deleteConfirmText === 'EXCLUIR' ? '#c94a2f' : '#e5ddc9', color: deleteConfirmText === 'EXCLUIR' ? '#fff' : '#aaa', fontWeight: 700, fontSize: '14px', cursor: deleteConfirmText === 'EXCLUIR' ? 'pointer' : 'not-allowed', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s' }}>
                {deletingVehicle ? 'Removendo...' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [NEW] Modal com o link de repasse pra mandar pro comprador */}
      {transferModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setTransferModalOpen(false); }}>
          <div style={{ background: '#fff', borderRadius: '22px', padding: m ? '24px' : '32px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 48px rgba(15,64,35,.14)', boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'center', marginBottom: '18px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#e7f0ea', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '26px' }}>🔁</div>
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '20px', color: '#14562e', margin: '0 0 6px' }}>Repassar veículo</h3>
              <p style={{ fontSize: '13px', color: '#6a7268', margin: 0, lineHeight: 1.5 }}>
                Manda esse link pra quem comprou o carro (WhatsApp, por exemplo). A NICO não participa da venda em si — o link só transfere o histórico depois que o comprador aceitar, logado na própria conta dele.
              </p>
            </div>

            <div style={{ background: '#f4ede0', borderRadius: '12px', padding: '14px', marginBottom: '14px', wordBreak: 'break-all', fontSize: '13px', color: '#183d24', fontFamily: 'monospace' }}>
              {transferLink?.url}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <button onClick={copyTransferLink}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: '#14562e', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                Copiar link
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Segue o histórico do ${vehicle.brand} ${vehicle.model} que te vendi — é só abrir esse link e aceitar na sua conta NICO: ${transferLink?.url || ''}`)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1.5px solid #e5ddc9', background: '#fff', color: '#183d24', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", textAlign: 'center', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                Mandar por WhatsApp
              </a>
            </div>

            <p style={{ fontSize: '11.5px', color: '#8b9188', textAlign: 'center', margin: '0 0 14px' }}>
              Válido por 30 dias{transferLink?.expires_at ? ` (até ${new Date(transferLink.expires_at).toLocaleDateString('pt-BR')})` : ''}. Gerar um novo link cancela este.
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setTransferModalOpen(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1.5px solid #e5ddc9', background: '#fff', color: '#183d24', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                Fechar
              </button>
              <button onClick={handleCancelTransfer} disabled={cancellingTransfer}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: 'rgba(200,50,40,.12)', color: '#c94a2f', fontWeight: 700, fontSize: '14px', cursor: cancellingTransfer ? 'default' : 'pointer', fontFamily: "'DM Sans', sans-serif", opacity: cancellingTransfer ? 0.7 : 1 }}>
                {cancellingTransfer ? 'Cancelando...' : 'Cancelar repasse'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [NEW] Preview de foto/vídeo de comprovação do serviço (ver ícones 📷/🎥 no histórico) */}
      {mediaPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setMediaPreview(null); }}>
          <div style={{ background: '#fff', borderRadius: '22px', padding: '16px', maxWidth: '520px', width: '100%', boxShadow: '0 20px 48px rgba(15,64,35,.14)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <button onClick={() => setMediaPreview(null)}
                style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f4ede0', border: 'none', cursor: 'pointer', color: '#183d24', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {mediaPreview.type === 'photo' ? (
              <img src={mediaPreview.url} alt="Foto do reparo" style={{ width: '100%', borderRadius: '14px', display: 'block' }} />
            ) : (
              <video src={mediaPreview.url} controls autoPlay style={{ width: '100%', borderRadius: '14px', display: 'block' }} />
            )}
          </div>
        </div>
      )}

      <ServiceModal
        open={serviceModalOpen}
        onClose={handleCloseModal}
        vehicles={[vehicle]}
        onSuccess={loadData}
        editService={editingService}
      />
    </div>
  );
}
